export interface PerformanceMetrics {
  fps: number
  frameTime: number
  stability: number // 0-1, how stable the FPS is
  deviceClass: 'high' | 'medium' | 'low'
}

export function detectDeviceClass(): 'high' | 'medium' | 'low' {
  // More reliable device detection using multiple signals
  let score = 0

  // 1. Hardware concurrency (CPU cores)
  const cores = navigator.hardwareConcurrency || 2
  if (cores >= 8) score += 3
  else if (cores >= 4) score += 2
  else if (cores >= 2) score += 1

  // 2. Device memory (if available, but don't rely on it heavily)
  if ('deviceMemory' in navigator) {
    const deviceMemory = (navigator as any).deviceMemory
    if (deviceMemory >= 8) score += 2
    else if (deviceMemory >= 4) score += 1
  } else {
    score += 1 // Neutral if unknown
  }

  // 3. Connection type (affects performance expectations)
  if ('connection' in navigator) {
    const connection = (navigator as any).connection
    if (connection?.effectiveType === '4g') score += 1
    else if (connection?.effectiveType === '3g') score -= 1
  }

  // 4. User agent hints for mobile/low-power devices
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
  if (isMobile) score -= 1

  // 5. Screen size (larger screens often mean more powerful devices)
  const screenArea = screen.width * screen.height
  if (screenArea > 2073600)
    score += 1 // 1920x1080+
  else if (screenArea < 921600) score -= 1 // Below 1280x720

  // Classification
  if (score >= 6) return 'high'
  else if (score >= 3) return 'medium'
  else return 'low'
}

export function monitorPerformance(duration = 2000): Promise<PerformanceMetrics> {
  return new Promise((resolve) => {
    const startTime = performance.now()
    const frameTimes: number[] = []
    let lastFrameTime = startTime
    let frameCount = 0

    function measureFrame() {
      const now = performance.now()
      const frameTime = now - lastFrameTime
      frameTimes.push(frameTime)
      lastFrameTime = now
      frameCount++

      const elapsed = now - startTime
      if (elapsed >= duration) {
        // Calculate metrics
        const totalTime = elapsed / 1000
        const avgFPS = frameCount / totalTime
        const avgFrameTime = frameTimes.reduce((sum, ft) => sum + ft, 0) / frameTimes.length

        // Calculate stability (low variance = high stability)
        const frameTimeVariance =
          frameTimes.reduce((sum, ft) => sum + Math.pow(ft - avgFrameTime, 2), 0) /
          frameTimes.length
        const stability = Math.max(0, 1 - frameTimeVariance / (avgFrameTime * avgFrameTime))

        resolve({
          fps: Math.round(avgFPS),
          frameTime: Math.round(avgFrameTime * 100) / 100,
          stability: Math.round(stability * 100) / 100,
          deviceClass: detectDeviceClass(),
        })
        return
      }

      requestAnimationFrame(measureFrame)
    }

    requestAnimationFrame(measureFrame)
  })
}

// Legacy functions for backward compatibility
export function monitorFPS(): Promise<number> {
  return monitorPerformance(1000).then((metrics) => metrics.fps)
}

export function monitorRAM(): Promise<{ usedJSHeapSize: number; totalJSHeapSize: number }> {
  return new Promise((resolve, reject) => {
    if (performance && (performance as any).memory) {
      const memory = (performance as any).memory
      resolve({
        usedJSHeapSize: memory.usedJSHeapSize / (1024 * 1024),
        totalJSHeapSize: memory.totalJSHeapSize / (1024 * 1024),
      })
    } else {
      reject(new Error('Memory API is not supported in this environment.'))
    }
  })
}
